import React, { useState } from 'react';
import {
  Box,
  VStack,
  Text,
  Input,
  Select,
  Button,
  FormLabel,
  IconButton,
  Flex,
  Icon,
  Tag
} from '@chakra-ui/react';
import { FaPlus, FaTrash } from 'react-icons/fa';
import { FiCode } from 'react-icons/fi';
import VariablePickerDrawer from '../VariablePickerDrawer';

// ── Config ───────────────────────────────────────────────────────────────────

const BASE_COLORS = ['blue', 'purple', 'green', 'orange', 'pink'];
const GROUP_LABELS = 'ABCDEFGHIJKLMNOPQRST'.split('');
const MAX_GROUPS = 20;

const GROUP_COLORS = GROUP_LABELS.map((letter, i) => ({
  name: BASE_COLORS[i % BASE_COLORS.length],
  label: `Group ${letter}`,
}));

const CHAKRA_COLOR_MAP = {
  blue:   { scheme: 'blue',   border: 'blue.200',   bg: 'blue.50',   text: 'blue.700',   dot: 'blue.500'   },
  purple: { scheme: 'purple', border: 'purple.200', bg: 'purple.50', text: 'purple.700', dot: 'purple.500' },
  green:  { scheme: 'green',  border: 'green.200',  bg: 'green.50',  text: 'green.700',  dot: 'green.500'  },
  orange: { scheme: 'orange', border: 'orange.200', bg: 'orange.50', text: 'orange.700', dot: 'orange.500' },
  pink:   { scheme: 'pink',   border: 'pink.200',   bg: 'pink.50',   text: 'pink.700',   dot: 'pink.500'   },
};

const getOperatorsForType = (type) => {
  if (type === 'number') return [
    { value: 'equals',        label: 'Equals (=)'           },
    { value: 'not_equals',    label: 'Not Equals (≠)'       },
    { value: 'greater_than',  label: 'Greater Than (>)'     },
    { value: 'less_than',     label: 'Less Than (<)'        },
    { value: 'greater_equal', label: 'Greater or Equal (≥)' },
    { value: 'less_equal',    label: 'Less or Equal (≤)'    },
    { value: 'exists',        label: 'Exists'               },
    { value: 'not_exists',    label: 'Not Exists'           },
  ];
  if (type === 'boolean') return [
    { value: 'is_true',    label: 'Is True'    },
    { value: 'is_false',   label: 'Is False'   },
    { value: 'exists',     label: 'Exists'     },
    { value: 'not_exists', label: 'Not Exists' },
  ];
  return [
    { value: 'equals',       label: 'Equals'      },
    { value: 'not_equals',   label: 'Not Equals'  },
    { value: 'contains',     label: 'Contains'    },
    { value: 'not_contains', label: 'Not Contains' },
    { value: 'starts_with',  label: 'Starts With' },
    { value: 'ends_with',    label: 'Ends With'   },
    { value: 'exists',       label: 'Exists'      },
    { value: 'not_exists',   label: 'Not Exists'  },
  ];
};

const inferType = (path = '') => {
  const lower = path.toLowerCase();
  if (['count', 'amount', 'total', 'price', 'quantity', 'age', 'score', 'number'].some(k => lower.includes(k))) return 'number';
  if (['is_', 'has_', 'enabled', 'active', 'verified', 'bool'].some(k => lower.includes(k))) return 'boolean';
  return 'string';
};

const NO_VALUE_OPERATORS = ['is_true', 'is_false', 'exists', 'not_exists'];

// ── Component ────────────────────────────────────────────────────────────────

const SmartConditionNodeProperties = ({ nodeConfig, setNodeConfig, updateNodeData, nodes }) => {
  const [varPickerOpen, setVarPickerOpen] = useState(false);
  const [activeGroupId, setActiveGroupId] = useState(null);
  const [activeRuleId, setActiveRuleId] = useState(null);
  const [showTestPanel, setShowTestPanel] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // nodeConfig is single source of truth
  const groups = nodeConfig.groups || [];
  const defaultLabel = nodeConfig.defaultLabel ?? 'No Match';

  const setGroups = (newGroups) => setNodeConfig({ ...nodeConfig, groups: newGroups });
  const setDefaultLabel = (val) => setNodeConfig({ ...nodeConfig, defaultLabel: val });

  // ── Group ops ──────────────────────────────────────────────────────────────
  const addGroup = () => {
    if (groups.length >= MAX_GROUPS) return;
    const c = GROUP_COLORS[groups.length];
    setGroups([...groups, { id: `group_${Date.now()}`, label: c.label, color: c.name, rules: [] }]);
  };

  const removeGroup = (groupId) => setGroups(groups.filter((g) => g.id !== groupId));

  const updateGroupLabel = (groupId, label) =>
    setGroups(groups.map((g) => (g.id === groupId ? { ...g, label } : g)));

  // ── Rule ops ───────────────────────────────────────────────────────────────
  const addRule = (groupId) =>
    setGroups(groups.map((g) =>
      g.id !== groupId ? g : {
        ...g,
        rules: [...g.rules, { id: `rule_${Date.now()}`, variable: '', variablePath: '', variableType: 'string', operator: 'equals', value: '' }],
      }
    ));

  const removeRule = (groupId, ruleId) =>
    setGroups(groups.map((g) =>
      g.id !== groupId ? g : { ...g, rules: g.rules.filter((r) => r.id !== ruleId) }
    ));

  const updateRule = (groupId, ruleId, field, value) =>
    setGroups(groups.map((g) =>
      g.id !== groupId ? g : {
        ...g,
        rules: g.rules.map((r) => {
          if (r.id !== ruleId) return r;
          const updated = { ...r, [field]: value };
          if (field === 'variableType') updated.operator = 'equals';
          return updated;
        }),
      }
    ));

  const updateRuleFields = (groupId, ruleId, fields) =>
    setGroups(groups.map((g) =>
      g.id !== groupId ? g : {
        ...g,
        rules: g.rules.map((r) =>
          r.id !== ruleId ? r : { ...r, ...fields }
        ),
      }
    ));

  // ── Variable picker ────────────────────────────────────────────────────────
  const openVarPicker = (groupId, ruleId) => {
    setActiveGroupId(groupId);
    setActiveRuleId(ruleId);
    setVarPickerOpen(true);
  };

  const handleVarSelect = (varPath) => {
    if (!activeGroupId || !activeRuleId) return;
    setGroups(groups.map((g) =>
      g.id !== activeGroupId ? g : {
        ...g,
        rules: g.rules.map((r) =>
          r.id !== activeRuleId ? r : {
            ...r, variable: varPath, variablePath: varPath,
            variableType: inferType(varPath), operator: 'equals', value: '',
          }
        ),
      }
    ));
    setVarPickerOpen(false);
    setActiveGroupId(null);
    setActiveRuleId(null);
  };

  // ── Test ───────────────────────────────────────────────────────────────────
  const runTest = () => {
    const hasValid = groups.some((g) => g.rules.length > 0 && g.rules.every((r) => r.variable && r.operator));
    if (hasValid) {
      const matched = groups.find((g) => g.rules.length > 0);
      setTestResult({ success: true, message: 'Conditions evaluated successfully!', path: matched?.label });
    } else {
      setTestResult({ success: false, message: 'No valid conditions to test. Add variables and operators to your groups.', path: defaultLabel });
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <VStack spacing={4} align="stretch">

      {/* Step Name */}
      <Box>
        <FormLabel fontSize="sm">Step Name</FormLabel>
        <Input
          value={nodeConfig.label || ''}
          onChange={(e) => setNodeConfig({ ...nodeConfig, label: e.target.value })}
          placeholder="e.g., Route by Customer Tier"
          size="sm"
        />
      </Box>

      {/* ── Condition Groups ─────────────────────────────────────────────── */}
      <Box>
        <Flex justify="space-between" align="center" mb={3}>
          <FormLabel fontSize="sm" fontWeight="semibold" mb={0}>
            Condition Groups (OR Logic)
          </FormLabel>
          <Tag size="sm" colorScheme="blue">
            If ANY group matches
          </Tag>
        </Flex>

        <VStack spacing={3} align="stretch">
          {groups.map((group, groupIndex) => {
            const cc = CHAKRA_COLOR_MAP[group.color] || CHAKRA_COLOR_MAP.blue;
            return (
              <Box
                key={group.id}
                border="2px solid"
                borderColor={cc.border}
                borderRadius="lg"
                overflow="hidden"
              >
                {/* Card Header */}
                <Box bg={cc.bg} px={3} py={3} borderBottom="1px solid" borderColor={cc.border}>
                  <Flex align="center" gap={3}>
                    <Box w={3} h={3} borderRadius="full" bg={cc.dot} flexShrink={0} />
                    <Input
                      value={group.label}
                      onChange={(e) => updateGroupLabel(group.id, e.target.value)}
                      size="sm"
                      flex={1}
                      fontWeight="semibold"
                      color={cc.text}
                      bg="white"
                      border="1px solid"
                      borderColor={cc.border}
                      _focus={{ boxShadow: 'none', borderColor: cc.border }}
                      h={8}
                      placeholder={`Group ${groupIndex + 1}`}
                    />
                    {groups.length > 1 && (
                      <IconButton
                        icon={<Icon as={FaTrash} />}
                        size="xs"
                        colorScheme="red"
                        variant="ghost"
                        onClick={() => removeGroup(group.id)}
                        aria-label="Remove group"
                        flexShrink={0}
                      />
                    )}
                  </Flex>
                  <Text fontSize="xs" color="gray.600" mt={2}>
                    All conditions in this group must match (AND logic)
                  </Text>
                </Box>

                {/* Card Content */}
                <Box p={3}>
                  <VStack spacing={3} align="stretch">
                    {/* Empty state */}
                    {group.rules.length === 0 ? (
                      <Box
                        textAlign="center"
                        py={6}
                        border="2px dashed"
                        borderColor="gray.200"
                        borderRadius="md"
                      >
                        <Text fontSize="xs" color="gray.400">
                          No conditions in this group. Click "Add Condition" below.
                        </Text>
                      </Box>
                    ) : (
                      group.rules.map((rule, ruleIndex) => (
                        <Box
                          key={rule.id}
                          bg="white"
                          border="2px solid"
                          borderColor="gray.200"
                          borderRadius="lg"
                          p={3}
                        >
                          {/* IF/AND badge + delete */}
                          <Flex justify="space-between" align="center" mb={2}>
                            <Tag size={"sm"} bg={"orange.400"} color={"white"}>
                              {ruleIndex === 0 ? 'IF' : 'AND'}
                            </Tag>
                            <IconButton
                              icon={<Icon as={FaTrash} />}
                              size="xs"
                              colorScheme="red"
                              variant="ghost"
                              onClick={() => removeRule(group.id, rule.id)}
                              aria-label="Remove condition"
                            />
                          </Flex>

                          {/* Variable */}
                          <Box mb={2}>
                            <FormLabel fontSize="xs" mb={1}>Variable</FormLabel>
                            <Box position="relative">
                              <Input
                                size="sm"
                                placeholder="Type path or click {x} to pick..."
                                value={rule.variablePath || ''}
                                onChange={(e) => {
                                  const path = e.target.value;
                                  updateRuleFields(group.id, rule.id, {
                                    variable: path,
                                    variablePath: path,
                                    variableType: inferType(path),
                                  });
                                }}
                                fontFamily="mono"
                                fontSize="xs"
                                pr="36px"
                                h={9}
                                bg={rule.variablePath ? 'blue.50' : undefined}
                                borderColor={rule.variablePath ? 'blue.200' : undefined}
                                color={rule.variablePath ? 'blue.700' : undefined}
                              />
                              <IconButton
                                icon={<Icon as={FiCode} />}
                                size="xs"
                                variant="ghost"
                                colorScheme="blue"
                                position="absolute"
                                right={1}
                                top="50%"
                                transform="translateY(-50%)"
                                zIndex={1}
                                onClick={() => openVarPicker(group.id, rule.id)}
                                aria-label="Pick variable"
                              />
                            </Box>
                            {rule.variablePath && (
                              <Text fontSize="10px" color="blue.500" mt={0.5} fontFamily="mono">
                                {`{{${rule.variablePath}}}`}
                              </Text>
                            )}
                          </Box>

                          {/* Operator */}
                          <Box mb={2}>
                            <FormLabel fontSize="xs" mb={1}>Operator</FormLabel>
                            <Select
                              size="sm"
                              value={rule.operator}
                              onChange={(e) => updateRule(group.id, rule.id, 'operator', e.target.value)}
                              h={9}
                            >
                              {getOperatorsForType(rule.variableType).map((op) => (
                                <option key={op.value} value={op.value}>{op.label}</option>
                              ))}
                            </Select>
                          </Box>

                          {/* Value */}
                          {!NO_VALUE_OPERATORS.includes(rule.operator) && (
                            <Box>
                              <FormLabel fontSize="xs" mb={1}>Value</FormLabel>
                              <Input
                                size="sm"
                                h={9}
                                type={rule.variableType === 'number' ? 'number' : 'text'}
                                value={rule.value}
                                onChange={(e) => updateRule(group.id, rule.id, 'value', e.target.value)}
                                placeholder={
                                  rule.variableType === 'number' ? '0' :
                                  rule.variableType === 'boolean' ? 'true/false' :
                                  'Enter value...'
                                }
                              />
                            </Box>
                          )}
                        </Box>
                      ))
                    )}

                    {/* Add Condition — dashed border */}
                    <Button
                      size="sm"
                      w="full"
                      variant="outline"
                      border="1px dashed"
                      borderColor={cc.border}
                      color={cc.text}
                      bg="transparent"
                      _hover={{ bg: cc.bg }}
                      leftIcon={<Icon as={FaPlus} />}
                      onClick={() => addRule(group.id)}
                      h={9}
                      fontSize="xs"
                    >
                      Add Condition to {group.label}
                    </Button>
                  </VStack>
                </Box>
              </Box>
            );
          })}

          {/* Add OR Group — filled orange */}
          <Button
            w="full"
            h={10}
            colorScheme="orange"
            onClick={addGroup}
            isDisabled={groups.length >= MAX_GROUPS}
            leftIcon={<Icon as={FaPlus} />}
            fontSize="sm"
          >
            Add OR Group
          </Button>
          {groups.length >= MAX_GROUPS && (
            <Text fontSize="xs" color="gray.500" textAlign="center">
              Maximum {MAX_GROUPS} groups reached
            </Text>
          )}
        </VStack>
      </Box>

      {/* ── Default Path ─────────────────────────────────────────────────── */}
      <Box border="1px solid" borderColor="gray.200" borderRadius="lg" overflow="hidden">
        <Box px={3} py={3} borderBottom="1px solid" borderColor="gray.100">
          <Text fontSize="sm" fontWeight="semibold">Default Path</Text>
          <Text fontSize="xs" color="gray.500" mt={0.5}>Used when no groups match</Text>
        </Box>
        <Box p={3}>
          <Box bg="gray.50" border="1px solid" borderColor="gray.200" borderRadius="lg" p={3}>
            <Flex align="center" gap={2} mb={2}>
              <Box w={2} h={2} borderRadius="full" bg="gray.400" />
              <Text fontSize="xs" fontWeight="semibold" color="gray.700">If no groups match</Text>
            </Flex>
            <Input
              size="sm"
              bg="white"
              value={defaultLabel}
              onChange={(e) => setDefaultLabel(e.target.value)}
              placeholder="No Match"
              h={9}
            />
          </Box>
        </Box>
      </Box>

     

      {/* ── Save Button ───────────────────────────────────────────────────── */}
      <Button
        w="full"
        h={9}
        bg="#0057FF"
        color="white"
        _hover={{ bg: '#0047DD' }}
        fontSize="sm"
        onClick={updateNodeData}
      >
        Save Configuration
      </Button>

      {/* Variable Picker Drawer */}
      <VariablePickerDrawer
        isOpen={varPickerOpen}
        onClose={() => { setVarPickerOpen(false); setActiveGroupId(null); setActiveRuleId(null); }}
        onSelect={handleVarSelect}
        flowNodes={nodes || []}
      />
    </VStack>
  );
};

export default SmartConditionNodeProperties;
